import { useQuery, useQueryClient } from "react-query";
import { defaultStaleTime } from "../withQueryData";
import { usePropertiesApi } from "./usePropertiesApi";

export const queryKeys = {
    properties: ['properties'],
    property: (id) => ['property', { propertyId: id }],
};

export const usePropertiesData = (propertyId) => {
    const { findAllProperties, findPropertyById, createProperty, updateProperty, deleteProperty } = usePropertiesApi();

    const queryClient = useQueryClient();

    const cachedProperties = queryClient.getQueryData(queryKeys.properties);

    const queryData = useQuery({
        queryKey: propertyId ? queryKeys.property(propertyId) : queryKeys.properties,
        queryFn: propertyId ? () => findPropertyById(propertyId) : findAllProperties,
        initialData: propertyId ? () => cachedProperties?.find(p => p.id === propertyId) : undefined,
        staleTime: defaultStaleTime,
    });

    const _createProperty = async (property, logoFile) => {
        const createdProperty = await createProperty(property, logoFile);

        queryClient.setQueryData(queryKeys.property(createdProperty.id), createdProperty);

        const cachedAll = queryClient.getQueryData(queryKeys.properties);
        if (cachedAll) {
            queryClient.setQueryData(queryKeys.properties, [...cachedAll, createdProperty]);
        }

        return createdProperty;
    };

    const _updateProperty = async (id, property) => {
        const updatedProperty = await updateProperty(id, property);

        queryClient.setQueryData(queryKeys.property(updatedProperty.id), updatedProperty);

        const cachedAll = queryClient.getQueryData(queryKeys.properties);
        if (cachedAll) {
            queryClient.setQueryData(
                queryKeys.properties,
                cachedAll.map(p => p.id === updatedProperty.id ? updatedProperty : p)
            );
        }

        return updatedProperty;
    };

    const _deleteProperty = async (id) => {
        await deleteProperty(id);

        queryClient.removeQueries(queryKeys.property(id));

        const cachedAll = queryClient.getQueryData(queryKeys.properties);
        if (cachedAll) {
            queryClient.setQueryData(queryKeys.properties, cachedAll.filter(p => p.id !== id));
        }
    };

    return {
        queryData,
        createProperty: _createProperty,
        updateProperty: _updateProperty,
        deleteProperty: _deleteProperty,
    };
};
